import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useMyPermissions, permissionPayloadAllows } from '@/auth/permissions';
import { PermissionDenied } from '@/components/PermissionDenied';
import { Loader2 } from 'lucide-react';

/**
 * Staff session gate. Optional RBAC:
 *   need="admin.users.manage"
 *   anyOf={['candidate.view_all', 'candidate.view_assigned']}
 *   allOf={['a', 'b']}
 *
 * Prefer permission codes over legacy ``roles`` — Roles & Permissions matrix
 * grants must work for non-admin users.role values.
 */
export default function RequireAuth({
  roles,
  permissions,
  need,
  anyOf,
  allOf,
  children,
}) {
  const { user, loading, isAuthenticated, hasRole, hasPermission } = useAuth();
  const loc = useLocation();
  const needsRbacCodes = !!(need || (anyOf && anyOf.length) || (allOf && allOf.length));
  const { data: myPerms, isLoading: permsLoading, isError: permsError } = useMyPermissions();

  if (loading) {
    return (
      <div className="grid min-h-screen place-items-center">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Loading session…</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const next = encodeURIComponent(loc.pathname + loc.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  if (roles && roles.length && !hasRole(...roles)) {
    return (
      <PermissionDenied
        testId="forbidden-card"
        title="Access denied"
        message={`This area requires one of: ${roles.join(', ')}. You are ${user?.role || 'unknown'}.`}
      />
    );
  }

  // Legacy login-token permission list (rarely populated). Prefer need/anyOf.
  if (permissions && permissions.length && !permissions.every((p) => hasPermission(p))) {
    return (
      <PermissionDenied
        title="Access denied"
        message="You don’t have permission to view this page."
      />
    );
  }

  if (needsRbacCodes) {
    if (permsLoading && !myPerms) {
      return (
        <div className="grid min-h-[40vh] place-items-center">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Checking permissions…</span>
          </div>
        </div>
      );
    }
    if (permsError && !myPerms) {
      return (
        <PermissionDenied
          title="Access denied"
          message="Could not verify your permissions. Try refreshing the page."
        />
      );
    }
    if (!permissionPayloadAllows(myPerms, { need, anyOf, allOf })) {
      return (
        <PermissionDenied
          title="Access denied"
          message="You don’t have permission to view this page."
        />
      );
    }
  }

  return children;
}
